import { zodResolver } from '@hookform/resolvers/zod';
import {
  FEEDBACK_DIAGNOSTIC_SECTIONS,
  FEEDBACK_MAX_SCREENSHOT_BYTES,
  FEEDBACK_REPRODUCIBILITY,
  FEEDBACK_TYPES,
  FEEDBACK_URGENCIES,
  feedbackCreatePayloadSchema,
  type AuthenticatedUser,
  type FeedbackCreatePayload,
  type FeedbackDiagnosticSection,
} from '@erp/shared';
import {
  Camera,
  CheckCircle2,
  ChevronDown,
  Info,
  RotateCcw,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useSession } from '@/features/auth';
import { useSubmitFeedback } from '@/features/feedback/api/use-feedback';
import {
  collectFeedbackDiagnostics,
  diagnosticServerContextPreview,
  recordFeedbackBreadcrumb,
} from '@/lib/feedback-diagnostics';
import { toastApiError } from '@/lib/api-error-toast';
import { useTheme } from '@/lib/theme';

interface MaskRectangle {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

interface ScreenshotEditorHandle {
  exportImage: () => Promise<Blob | null>;
  clear: () => void;
}

interface ScreenshotEditorProps {
  readonly onDirtyChange: (dirty: boolean) => void;
}

const ScreenshotEditor = forwardRef<ScreenshotEditorHandle, ScreenshotEditorProps>(
  function ScreenshotEditor({ onDirtyChange }, ref) {
    const { t } = useTranslation();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imageRef = useRef<ImageBitmap | null>(null);
    const dragStart = useRef<{ x: number; y: number } | null>(null);
    const draftMaskRef = useRef<MaskRectangle | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [masks, setMasks] = useState<readonly MaskRectangle[]>([]);
    const [draftMask, setDraftMask] = useState<MaskRectangle | null>(null);
    const [error, setError] = useState<string | null>(null);

    const redraw = useCallback((): void => {
      const canvas = canvasRef.current;
      const image = imageRef.current;
      if (canvas === null || image === null) return;
      const context = canvas.getContext('2d');
      if (context === null) return;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      context.fillStyle = '#111827';
      for (const mask of [...masks, ...(draftMask === null ? [] : [draftMask])]) {
        context.fillRect(mask.x, mask.y, mask.width, mask.height);
      }
    }, [draftMask, masks]);

    useEffect(redraw, [redraw]);

    const clear = useCallback((): void => {
      imageRef.current?.close();
      imageRef.current = null;
      setFileName(null);
      setMasks([]);
      setDraftMask(null);
      draftMaskRef.current = null;
      setError(null);
      onDirtyChange(false);
      const canvas = canvasRef.current;
      if (canvas !== null) {
        canvas.width = 0;
        canvas.height = 0;
      }
    }, [onDirtyChange]);

    useImperativeHandle(
      ref,
      () => ({
        exportImage: () =>
          new Promise((resolve) => {
            const canvas = canvasRef.current;
            if (canvas === null || imageRef.current === null) {
              resolve(null);
              return;
            }
            redraw();
            canvas.toBlob((blob) => resolve(blob), 'image/png', 0.92);
          }),
        clear,
      }),
      [clear, redraw],
    );

    const coordinates = (event: ReactPointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
      const canvas = event.currentTarget;
      const bounds = canvas.getBoundingClientRect();
      return {
        x: ((event.clientX - bounds.left) / bounds.width) * canvas.width,
        y: ((event.clientY - bounds.top) / bounds.height) * canvas.height,
      };
    };

    return (
      <section className="space-y-3 rounded-md border border-border p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-medium">{t('feedback.screenshot.title')}</h3>
            <p className="text-xs text-muted-foreground">{t('feedback.screenshot.hint')}</p>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" asChild>
              <Label className="cursor-pointer">
                <Camera className="h-4 w-4" aria-hidden="true" />
                {t('feedback.screenshot.choose')}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="sr-only"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file === undefined) return;
                    if (file.size > FEEDBACK_MAX_SCREENSHOT_BYTES) {
                      setError(t('feedback.screenshot.invalid'));
                      return;
                    }
                    setError(null);
                    void createImageBitmap(file)
                      .then((image) => {
                        imageRef.current?.close();
                        imageRef.current = image;
                        const scale = Math.min(1, 1600 / Math.max(image.width, image.height));
                        const canvas = canvasRef.current;
                        if (canvas !== null) {
                          canvas.width = Math.max(1, Math.round(image.width * scale));
                          canvas.height = Math.max(1, Math.round(image.height * scale));
                        }
                        setFileName(file.name);
                        setMasks([]);
                        setDraftMask(null);
                        draftMaskRef.current = null;
                        onDirtyChange(true);
                        requestAnimationFrame(redraw);
                      })
                      .catch(() => setError(t('feedback.screenshot.invalid')));
                  }}
                />
              </Label>
            </Button>
            {fileName === null ? null : (
              <Button type="button" variant="ghost" size="sm" onClick={clear}>
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                {t('feedback.screenshot.remove')}
              </Button>
            )}
          </div>
        </div>
        {error === null ? null : <p className="text-sm text-destructive">{error}</p>}
        {fileName === null ? null : (
          <>
            <div className="overflow-hidden rounded-md border border-border bg-muted">
              <canvas
                ref={canvasRef}
                className="block h-auto max-h-96 w-full touch-none object-contain"
                aria-label={t('feedback.screenshot.maskArea')}
                onPointerDown={(event) => {
                  event.currentTarget.setPointerCapture(event.pointerId);
                  dragStart.current = coordinates(event);
                }}
                onPointerMove={(event) => {
                  const start = dragStart.current;
                  if (start === null) return;
                  const current = coordinates(event);
                  const nextMask = {
                    x: Math.min(start.x, current.x),
                    y: Math.min(start.y, current.y),
                    width: Math.abs(current.x - start.x),
                    height: Math.abs(current.y - start.y),
                  };
                  draftMaskRef.current = nextMask;
                  setDraftMask(nextMask);
                }}
                onPointerUp={() => {
                  const completedMask = draftMaskRef.current;
                  if (
                    completedMask !== null &&
                    completedMask.width > 5 &&
                    completedMask.height > 5
                  ) {
                    setMasks((current) => [...current, completedMask]);
                  }
                  dragStart.current = null;
                  draftMaskRef.current = null;
                  setDraftMask(null);
                }}
              />
            </div>
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>{t('feedback.screenshot.maskInstructions')}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={masks.length === 0}
                onClick={() => setMasks([])}
              >
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                {t('feedback.screenshot.resetMasks')}
              </Button>
            </div>
          </>
        )}
      </section>
    );
  },
);

function useSmallScreen(): boolean {
  const [small, setSmall] = useState(() => window.matchMedia('(max-width: 639px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 639px)');
    const change = (): void => setSmall(query.matches);
    query.addEventListener('change', change);
    return () => query.removeEventListener('change', change);
  }, []);
  return small;
}

export interface FeedbackDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

const ALL_DIAGNOSTICS = [...FEEDBACK_DIAGNOSTIC_SECTIONS];

function defaultFeedbackValues(): FeedbackCreatePayload {
  return {
    type: 'IMPROVEMENT',
    title: '',
    description: '',
    attemptedAction: null,
    actualResult: null,
    expectedResult: null,
    reproducibility: null,
    reproductionSteps: null,
    urgency: 'NORMAL',
    affectedFeature: null,
    wantsFollowUp: false,
    includeExtendedDiagnostics: true,
    problematicFieldIds: [],
    diagnostics: {
      includedSections: ALL_DIAGNOSTICS,
      application: null,
      page: null,
      browser: null,
      breadcrumbs: [],
    },
  };
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps): JSX.Element | null {
  const { t } = useTranslation();
  const session = useSession();
  const { theme } = useTheme();
  const submit = useSubmitFeedback();
  const small = useSmallScreen();
  const screenshotRef = useRef<ScreenshotEditorHandle>(null);
  const [includedSections, setIncludedSections] =
    useState<readonly FeedbackDiagnosticSection[]>(ALL_DIAGNOSTICS);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [successReference, setSuccessReference] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [fieldIds, setFieldIds] = useState<readonly string[]>([]);
  const [screenshotDirty, setScreenshotDirty] = useState(false);

  const form = useForm<FeedbackCreatePayload>({
    resolver: zodResolver(feedbackCreatePayloadSchema),
    defaultValues: defaultFeedbackValues(),
  });
  const type = form.watch('type');
  const title = form.watch('title');
  const description = form.watch('description');

  useEffect(() => {
    if (!open) return;
    const values = [
      ...document.querySelectorAll<HTMLElement>(
        '[data-feedback-field], input[name], textarea[name], select[name]',
      ),
    ]
      .filter((element) => element.closest('[data-feedback-dialog]') === null)
      .map((element) => element.dataset['feedbackField'] ?? element.getAttribute('name') ?? '')
      .filter((value) => /^[a-zA-Z0-9][a-zA-Z0-9_.:/-]{0,119}$/u.test(value));
    setFieldIds([...new Set(values)]);
    recordFeedbackBreadcrumb({
      type: 'DIALOG',
      actionId: 'feedback.dialog.open',
      actionLabelKey: 'feedback.open',
      entityType: null,
      entityId: null,
      result: 'SUCCESS',
      errorTrackingId: null,
    });
  }, [open]);

  if (session.data === null || session.data === undefined) return null;
  const user = session.data as AuthenticatedUser;
  const diagnostics = collectFeedbackDiagnostics({ session: user, theme, includedSections });
  const preview = {
    verifiedServerContext: diagnosticServerContextPreview(user),
    clientDiagnostics: diagnostics,
  };

  const requestClose = (next: boolean): void => {
    if (next) {
      onOpenChange(true);
      return;
    }
    if (successReference === null && (form.formState.isDirty || screenshotDirty)) {
      setDiscardOpen(true);
      return;
    }
    recordFeedbackBreadcrumb({
      type: 'DIALOG',
      actionId: 'feedback.dialog.close',
      actionLabelKey: 'feedback.open',
      entityType: null,
      entityId: null,
      result: successReference === null ? 'CANCELLED' : 'SUCCESS',
      errorTrackingId: null,
    });
    onOpenChange(false);
  };

  const resetAndClose = (): void => {
    recordFeedbackBreadcrumb({
      type: 'DIALOG',
      actionId: 'feedback.dialog.close',
      actionLabelKey: 'feedback.open',
      entityType: null,
      entityId: null,
      result: successReference === null ? 'CANCELLED' : 'SUCCESS',
      errorTrackingId: null,
    });
    form.reset(defaultFeedbackValues());
    screenshotRef.current?.clear();
    setIncludedSections(ALL_DIAGNOSTICS);
    setDiagnosticsOpen(false);
    setSuccessReference(null);
    setScreenshotDirty(false);
    setIdempotencyKey(crypto.randomUUID());
    onOpenChange(false);
  };

  const fieldError = (name: keyof FeedbackCreatePayload): JSX.Element | null => {
    const message = form.formState.errors[name]?.message;
    return typeof message === 'string' ? (
      <p className="text-xs text-destructive" role="alert">
        {message}
      </p>
    ) : null;
  };

  const formContent =
    successReference === null ? (
      <form
        className="space-y-5"
        onSubmit={form.handleSubmit(
          async (values) => {
            const screenshot = (await screenshotRef.current?.exportImage()) ?? null;
            submit.mutate(
              { payload: { ...values, diagnostics }, screenshot, idempotencyKey },
              {
                onSuccess: (result) => {
                  setSuccessReference(result.reference);
                  form.reset(values);
                  toast.success(t('feedback.success.toast', { reference: result.reference }));
                  recordFeedbackBreadcrumb({
                    type: 'SAVE',
                    actionId: 'feedback.submit',
                    actionLabelKey: 'feedback.submit',
                    entityType: 'FEEDBACK',
                    entityId: result.id,
                    result: 'SUCCESS',
                    errorTrackingId: null,
                  });
                },
                onError: (error) => toastApiError(error),
              },
            );
          },
          () => {
            recordFeedbackBreadcrumb({
              type: 'VALIDATION_ERROR',
              actionId: 'feedback.validation.failed',
              actionLabelKey: 'feedback.submit',
              entityType: 'FEEDBACK',
              entityId: null,
              result: 'FAILED',
              errorTrackingId: null,
            });
          },
        )}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="feedback-type">{t('feedback.fields.type')}</Label>
            <Controller
              control={form.control}
              name="type"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="feedback-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FEEDBACK_TYPES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`feedback.types.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {fieldError('type')}
          </div>
          <div className="space-y-2">
            <Label htmlFor="feedback-urgency">{t('feedback.fields.urgency')}</Label>
            <Controller
              control={form.control}
              name="urgency"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="feedback-urgency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FEEDBACK_URGENCIES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`feedback.urgencies.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="feedback-title">{t('feedback.fields.title')}</Label>
            <span className="text-xs tabular-nums text-muted-foreground">{title.length}/160</span>
          </div>
          <Input id="feedback-title" maxLength={160} {...form.register('title')} />
          {fieldError('title')}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="feedback-description">{t('feedback.fields.description')}</Label>
            <span className="text-xs tabular-nums text-muted-foreground">
              {description.length}/10000
            </span>
          </div>
          <Textarea
            id="feedback-description"
            rows={5}
            maxLength={10_000}
            {...form.register('description')}
          />
          <p className="text-xs text-muted-foreground">{t('feedback.fields.noSecrets')}</p>
          {fieldError('description')}
        </div>

        {type === 'BUG' ? (
          <section className="space-y-4 rounded-md border border-border p-4">
            <h3 className="text-sm font-semibold">{t('feedback.bug.title')}</h3>
            {(['attemptedAction', 'actualResult', 'expectedResult'] as const).map((name) => (
              <div key={name} className="space-y-2">
                <Label htmlFor={`feedback-${name}`}>{t(`feedback.fields.${name}`)}</Label>
                <Textarea id={`feedback-${name}`} rows={3} {...form.register(name)} />
                {fieldError(name)}
              </div>
            ))}
            <div className="space-y-2">
              <Label htmlFor="feedback-reproducibility">
                {t('feedback.fields.reproducibility')}
              </Label>
              <Controller
                control={form.control}
                name="reproducibility"
                render={({ field }) => (
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger id="feedback-reproducibility">
                      <SelectValue placeholder={t('feedback.fields.choose')} />
                    </SelectTrigger>
                    <SelectContent>
                      {FEEDBACK_REPRODUCIBILITY.map((value) => (
                        <SelectItem key={value} value={value}>
                          {t(`feedback.reproducibility.${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {fieldError('reproducibility')}
            </div>
            <div className="space-y-2">
              <Label htmlFor="feedback-reproductionSteps">
                {t('feedback.fields.reproductionSteps')}
              </Label>
              <Textarea
                id="feedback-reproductionSteps"
                rows={4}
                {...form.register('reproductionSteps')}
              />
            </div>
          </section>
        ) : null}

        <div className="space-y-2">
          <Label htmlFor="feedback-feature">{t('feedback.fields.affectedFeature')}</Label>
          <Input id="feedback-feature" maxLength={160} {...form.register('affectedFeature')} />
        </div>

        {fieldIds.length === 0 ? null : (
          <section className="space-y-2">
            <Label>{t('feedback.fields.problematicFields')}</Label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {fieldIds.map((fieldId) => (
                <Controller
                  key={fieldId}
                  control={form.control}
                  name="problematicFieldIds"
                  render={({ field }) => (
                    <label className="flex min-h-11 items-center gap-2 rounded-md border px-3 text-sm">
                      <Checkbox
                        checked={field.value.includes(fieldId)}
                        onCheckedChange={(checked) =>
                          field.onChange(
                            checked === true
                              ? [...field.value, fieldId]
                              : field.value.filter((value) => value !== fieldId),
                          )
                        }
                      />
                      <span className="font-mono text-xs">{fieldId}</span>
                    </label>
                  )}
                />
              ))}
            </div>
          </section>
        )}

        <Controller
          control={form.control}
          name="wantsFollowUp"
          render={({ field }) => (
            <label className="flex min-h-11 items-center gap-2 rounded-md border px-3 text-sm">
              <Checkbox
                checked={field.value}
                onCheckedChange={(value) => field.onChange(value === true)}
              />
              {t('feedback.fields.wantsFollowUp')}
            </label>
          )}
        />

        <ScreenshotEditor ref={screenshotRef} onDirtyChange={setScreenshotDirty} />

        <section className="space-y-3 rounded-md border border-border p-3">
          <Controller
            control={form.control}
            name="includeExtendedDiagnostics"
            render={({ field }) => (
              <label className="flex min-h-11 items-center gap-2 text-sm">
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    const enabled = checked === true;
                    field.onChange(enabled);
                    setIncludedSections(enabled ? ALL_DIAGNOSTICS : []);
                  }}
                />
                {t('feedback.diagnostics.includeExtended')}
              </label>
            )}
          />
          <button
            type="button"
            className="flex min-h-11 w-full items-center justify-between gap-2 text-start"
            aria-expanded={diagnosticsOpen}
            onClick={() => setDiagnosticsOpen((current) => !current)}
          >
            <span className="flex items-center gap-2 text-sm font-medium">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              {t('feedback.diagnostics.title')}
            </span>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${diagnosticsOpen ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
          <p className="text-xs text-muted-foreground">{t('feedback.diagnostics.transparency')}</p>
          {diagnosticsOpen ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {FEEDBACK_DIAGNOSTIC_SECTIONS.map((section) => (
                  <label
                    key={section}
                    className="flex min-h-11 items-center gap-2 rounded-md border px-3 text-sm"
                  >
                    <Checkbox
                      checked={includedSections.includes(section)}
                      onCheckedChange={(checked) =>
                        setIncludedSections((current) =>
                          (() => {
                            const next =
                              checked === true
                                ? [...new Set([...current, section])]
                                : current.filter((value) => value !== section);
                            form.setValue('includeExtendedDiagnostics', next.length > 0, {
                              shouldDirty: true,
                            });
                            return next;
                          })(),
                        )
                      }
                    />
                    {t(`feedback.diagnostics.sections.${section}`)}
                  </label>
                ))}
              </div>
              <div className="flex items-start gap-2 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                {t('feedback.diagnostics.exclusions')}
              </div>
              <pre className="max-h-72 overflow-auto rounded-md bg-muted p-3 text-xs">
                {JSON.stringify(preview, null, 2)}
              </pre>
            </div>
          ) : null}
        </section>

        <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={() => requestClose(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={submit.isPending} data-feedback-action="feedback.submit">
            {submit.isPending ? t('feedback.submitting') : t('feedback.submit')}
          </Button>
        </div>
      </form>
    ) : (
      <div className="flex flex-col items-center gap-5 py-10 text-center" aria-live="polite">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <CheckCircle2 className="h-8 w-8 text-primary" aria-hidden="true" />
        </span>
        <div className="space-y-2">
          <h3 className="text-xl font-semibold">{t('feedback.success.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('feedback.success.description')}</p>
          <p className="font-mono text-lg font-semibold">{successReference}</p>
        </div>
        <Button type="button" onClick={resetAndClose}>
          {t('common.close')}
        </Button>
      </div>
    );

  return (
    <>
      {small ? (
        <Sheet open={open} onOpenChange={requestClose}>
          <SheetContent
            closeLabel={t('common.close')}
            className="w-full p-4 sm:p-6"
            data-feedback-dialog="true"
          >
            <SheetHeader className="pe-10">
              <SheetTitle>{t('feedback.title')}</SheetTitle>
              <SheetDescription>{t('feedback.description')}</SheetDescription>
            </SheetHeader>
            {formContent}
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={open} onOpenChange={requestClose}>
          <DialogContent
            closeLabel={t('common.close')}
            className="max-w-3xl"
            data-feedback-dialog="true"
          >
            <DialogHeader className="pe-10">
              <DialogTitle>{t('feedback.title')}</DialogTitle>
              <DialogDescription>{t('feedback.description')}</DialogDescription>
            </DialogHeader>
            {formContent}
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent>
          <AlertDialogTitle>{t('feedback.discard.title')}</AlertDialogTitle>
          <AlertDialogDescription>{t('feedback.discard.description')}</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('feedback.discard.continue')}</AlertDialogCancel>
            <AlertDialogAction onClick={resetAndClose}>
              {t('feedback.discard.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
